import type { FetchOptions } from 'ofetch';

/**
 * Dev-only API request logging. React Query DevTools shows query keys and
 * states but never the wire details — the resolved URL (base + path + query)
 * and the headers the interceptors attached — so this logs both from the
 * `ofetch` hooks every `@noor/api` client runs through.
 *
 * Enabled in DEV builds, silent in production and under test runners
 * (`MODE=test`, e.g. vitest). Override at runtime without a rebuild via
 * `localStorage`: `noor:api-log = "1"` forces it on, `"0"` forces it off.
 *
 * The `Authorization` header is redacted to its last 4 characters: enough to
 * correlate a request with a session while developing, never the full token
 * in a copy-pasted console.
 */

const FLAG_KEY = 'noor:api-log';

function readFlag(): string | null {
  try {
    if (!('localStorage' in globalThis)) return null;
    return globalThis.localStorage.getItem(FLAG_KEY);
  } catch {
    // Storage unavailable (SSR, blocked cookies) — fall through to the default.
    return null;
  }
}

/** Whether request/response lines should be logged right now. */
export function isApiLoggingEnabled(): boolean {
  const flag = readFlag();
  if (flag === '1' || flag === 'true') return true;
  if (flag === '0' || flag === 'false') return false;
  // `MODE=test` (vitest) implies DEV, so exclude it explicitly to keep test
  // output clean — the flag above still forces logging on when wanted.
  if (import.meta.env.MODE === 'test') return false;
  return import.meta.env.DEV === true;
}

function redactAuthValue(value: string): string {
  const match = /^(\S+)\s+(.+)$/.exec(value);
  if (!match) return '***';
  const token = match[2] ?? '';
  return token.length > 4 ? `${match[1]} ***${token.slice(-4)}` : `${match[1]} ***`;
}

/** Headers as a plain object, with `Authorization` redacted. */
export function redactHeaders(headers: Headers): Record<string, string> {
  const out: Record<string, string> = {};
  headers.forEach((value, key) => {
    out[key] = key.toLowerCase() === 'authorization' ? redactAuthValue(value) : value;
  });
  return out;
}

/** Render one query value the way the wire would, or skip it (`null`). */
function toQueryString(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }
  if (value === null || value === undefined) return null;
  try {
    const json = JSON.stringify(value);
    return typeof json === 'string' ? json : null;
  } catch {
    return null;
  }
}

function appendQuery(params: URLSearchParams, query: unknown): void {
  if (query === null || typeof query !== 'object') return;
  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        const rendered = toQueryString(item);
        if (rendered !== null) params.append(key, rendered);
      }
    } else {
      const rendered = toQueryString(value);
      if (rendered !== null) params.append(key, rendered);
    }
  }
}

/**
 * The URL that will actually be fetched: per-request `baseURL` (falling back
 * to the client's default) joined with the path, plus the serialized
 * `query`/`params`. Absolute request URLs skip the base join.
 */
export function resolveRequestUrl(
  request: string | URL | Request,
  options: Pick<FetchOptions<'json'>, 'baseURL' | 'query' | 'params'>,
  defaultBaseURL: string,
): string {
  const raw =
    typeof request === 'string'
      ? request
      : request instanceof URL
        ? request.href
        : request.url;
  const base = options.baseURL ?? defaultBaseURL;
  const joined =
    /^https?:\/\//.test(raw) || base === ''
      ? raw
      : `${base.replace(/\/$/, '')}/${raw.replace(/^\//, '')}`;
  const params = new URLSearchParams();
  appendQuery(params, options.query);
  appendQuery(params, options.params);
  const query = params.toString();
  return query === '' ? joined : `${joined}${joined.includes('?') ? '&' : '?'}${query}`;
}

/** `→ POST https://…` plus the redacted headers object. No-op when disabled. */
export function logApiRequest(method: string, url: string, headers: Headers): void {
  if (!isApiLoggingEnabled()) return;
  console.info(`[api] → ${method} ${url}`, redactHeaders(headers));
}

/** `← 200 POST https://…` — the status line for a completed request. */
export function logApiResponse(method: string, url: string, status: number): void {
  if (!isApiLoggingEnabled()) return;
  console.info(`[api] ← ${status} ${method} ${url}`);
}
